import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tabs,
  Tab,
  Alert,
  Divider,
  LinearProgress
} from '@mui/material';
import {
  Refresh,
  ArrowBack,
  EmojiEvents,
  CheckCircle,
  Schedule,
  TrendingUp,
  TrendingDown
} from '@mui/icons-material';

export default function MultiRoundTournament() {
  const { tournamentId } = useParams();
  const navigate = useNavigate();
  const [tournament, setTournament] = useState(null);
  const [players, setPlayers] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRound, setSelectedRound] = useState(0); // 0 = cumulative
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    fetchData();
  }, [tournamentId]);

  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        fetchData(true);
      }, 30000);
    }
    return () => clearInterval(interval);
  }, [autoRefresh, tournamentId]);

  const fetchData = async (silent = false) => {
    try {
      if (!silent) setLoading(true);

      const tournamentDoc = await getDoc(doc(db, 'tournaments', tournamentId));
      if (!tournamentDoc.exists()) {
        throw new Error('Tournament not found');
      }

      const tournamentData = { id: tournamentDoc.id, ...tournamentDoc.data() };
      setTournament(tournamentData);

      const playersQuery = query(
        collection(db, 'players'),
        where('organizationId', '==', tournamentData.organizationId)
      );
      const playersSnapshot = await getDocs(playersQuery);
      const playersData = playersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPlayers(playersData);

      const leaderboardData = calculateMultiRoundLeaderboard(tournamentData, playersData);
      setLeaderboard(leaderboardData);

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const calculateMultiRoundLeaderboard = (tournament, players) => {
    const rounds = tournament.rounds || [];
    const numRounds = tournament.numberOfRounds || 1;
    const currentRound = tournament.currentRound || 1;
    const tournamentType = tournament.type || 'STROKEPLAY_SCRATCH';

    const playersWithScores = players.map(player => {
      const roundScores = [];
      let cumulativeGross = 0;
      let cumulativeNet = 0;
      let totalHolesPlayed = 0;

      // Calculate scores for each round
      for (let i = 1; i <= numRounds; i++) {
        const roundData = rounds.find(r => r.roundNumber === i);
        const roundPlayerScore = roundData?.scores?.[player.id];
        
        if (roundPlayerScore) {
          const holes = Object.keys(roundPlayerScore);
          const roundGross = Object.values(roundPlayerScore).reduce((sum, score) => 
            sum + (parseInt(score) || 0), 0);
          
          let roundNet = roundGross;
          if (tournamentType === 'STROKEPLAY_HANDICAPPED' && player.handicap) {
            roundNet = roundGross - Math.floor(player.handicap);
          }

          roundScores.push({
            round: i,
            gross: roundGross,
            net: roundNet,
            holes: holes.length,
            completed: holes.length === 18
          });

          if (holes.length === 18) {
            cumulativeGross += roundGross;
            cumulativeNet += roundNet;
            totalHolesPlayed += 18;
          }
        } else {
          roundScores.push({
            round: i,
            gross: null,
            net: null,
            holes: 0,
            completed: false
          });
        }
      }

      const completedRounds = roundScores.filter(r => r.completed).length;
      
      return {
        id: player.id,
        name: `${player.firstName} ${player.lastName}`,
        handicap: player.handicap || 0,
        roundScores,
        cumulativeGross,
        cumulativeNet,
        completedRounds,
        totalHolesPlayed,
        scoringScore: tournamentType === 'STROKEPLAY_SCRATCH' ? cumulativeGross : cumulativeNet
      };
    });

    // Filter players with at least one completed round
    const playersWithCompletedRounds = playersWithScores.filter(p => p.completedRounds > 0);

    // Sort by cumulative score
    playersWithCompletedRounds.sort((a, b) => a.scoringScore - b.scoringScore);

    return playersWithCompletedRounds.map((player, index) => ({
      ...player,
      position: index + 1,
      previousPosition: player.previousPosition || index + 1
    }));
  };

  const handleActivateRound = async (roundNumber) => {
    if (!window.confirm(`Activate Round ${roundNumber}? This will allow score entry for this round.`)) return;

    try {
      await updateDoc(doc(db, 'tournaments', tournamentId), {
        currentRound: roundNumber,
        [`rounds.${roundNumber - 1}.status`]: 'active'
      });

      fetchData();
    } catch (error) {
      console.error('Error activating round:', error);
      alert('Error activating round: ' + error.message);
    }
  };

  const formatScoreToPar = (score, coursePar = 72) => {
    const diff = score - coursePar;
    if (diff === 0) return 'E';
    if (diff > 0) return `+${diff}`;
    return diff.toString();
  };

  const getRoundScoreForDisplay = (player, roundNum) => {
    const roundScore = player.roundScores.find(r => r.round === roundNum);
    if (!roundScore || !roundScore.completed) return '-';
    
    return tournament.type === 'STROKEPLAY_SCRATCH' 
      ? roundScore.gross 
      : roundScore.net;
  };

  const getPositionChange = (current, previous) => {
    if (current < previous) return <TrendingUp color="success" fontSize="small" />;
    if (current > previous) return <TrendingDown color="error" fontSize="small" />;
    return null;
  };

  if (loading) {
    return (
      <Container sx={{ mt: 4 }}>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  if (!tournament) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">Tournament not found</Alert>
      </Container>
    );
  }

  const numRounds = tournament.numberOfRounds || 1;
  const currentRound = tournament.currentRound || 1;
  const coursePar = tournament.golfCourse?.totalPar || 72;

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate('/tournaments')}>
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h4">{tournament.name}</Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Multi-Round Tournament - {numRounds} Rounds
            </Typography>
          </Box>
        </Box>
        <Box display="flex" gap={2} alignItems="center">
          <Chip
            icon={<Refresh />}
            label={autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
            onClick={() => setAutoRefresh(!autoRefresh)}
            color={autoRefresh ? 'success' : 'default'}
            variant={autoRefresh ? 'filled' : 'outlined'}
          />
          <IconButton onClick={() => fetchData()} color="primary">
            <Refresh />
          </IconButton>
        </Box>
      </Box>

      {/* Round Progress */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Tournament Progress</Typography>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Typography variant="body2" color="text.secondary">
            Round {currentRound} of {numRounds}
          </Typography>
          <LinearProgress 
            variant="determinate" 
            value={(currentRound / numRounds) * 100}
            sx={{ flexGrow: 1 }}
          />
          <Typography variant="body2" fontWeight="bold">
            {Math.round((currentRound / numRounds) * 100)}%
          </Typography>
        </Box>

        <Grid container spacing={2}>
          {Array.from({ length: numRounds }, (_, i) => i + 1).map(roundNum => {
            const roundData = tournament.rounds?.find(r => r.roundNumber === roundNum);
            const isActive = currentRound === roundNum;
            const isCompleted = roundNum < currentRound;

            return (
              <Grid item xs={6} sm={4} md={3} lg={2} key={roundNum}>
                <Card 
                  variant="outlined"
                  sx={{ 
                    borderColor: isActive ? 'primary.main' : 'default',
                    borderWidth: isActive ? 2 : 1,
                    bgcolor: isCompleted ? 'success.light' : 'inherit'
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="h6">Round {roundNum}</Typography>
                    <Box mt={1}>
                      {isCompleted && (
                        <Chip 
                          icon={<CheckCircle />} 
                          label="Complete" 
                          size="small" 
                          color="success"
                        />
                      )}
                      {isActive && (
                        <Chip 
                          icon={<Schedule />} 
                          label="Active" 
                          size="small" 
                          color="primary"
                        />
                      )}
                      {!isActive && !isCompleted && (
                        <Chip label="Upcoming" size="small" color="default" />
                      )}
                    </Box>
                    {roundData?.date && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        {new Date(roundData.date).toLocaleDateString()}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Paper>

      {/* Round Selector Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs 
          value={selectedRound} 
          onChange={(e, val) => setSelectedRound(val)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Cumulative" />
          {Array.from({ length: numRounds }, (_, i) => (
            <Tab key={i + 1} label={`Round ${i + 1}`} />
          ))}
        </Tabs>
      </Paper>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Players</Typography>
              <Typography variant="h6">{leaderboard.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Current Leader</Typography>
              <Typography variant="h6">{leaderboard[0]?.name || '-'}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Leading Score</Typography>
              <Typography variant="h6">
                {leaderboard[0] ? formatScoreToPar(leaderboard[0].scoringScore, coursePar * leaderboard[0].completedRounds) : '-'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>Course Par</Typography>
              <Typography variant="h6">{coursePar}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Leaderboard */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="center" width="80px"><strong>Pos</strong></TableCell>
              <TableCell><strong>Player</strong></TableCell>
              <TableCell align="center"><strong>HCP</strong></TableCell>
              {selectedRound === 0 ? (
                <>
                  {Array.from({ length: numRounds }, (_, i) => (
                    <TableCell key={i + 1} align="center">
                      <strong>R{i + 1}</strong>
                    </TableCell>
                  ))}
                  <TableCell align="center"><strong>Total</strong></TableCell>
                  <TableCell align="center"><strong>To Par</strong></TableCell>
                </>
              ) : (
                <>
                  <TableCell align="center"><strong>Holes</strong></TableCell>
                  <TableCell align="center"><strong>Score</strong></TableCell>
                  <TableCell align="center"><strong>To Par</strong></TableCell>
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {leaderboard.length === 0 ? (
              <TableRow>
                <TableCell colSpan={selectedRound === 0 ? 6 + numRounds : 6} align="center">
                  <Typography color="text.secondary">No scores recorded yet</Typography>
                </TableCell>
              </TableRow>
            ) : (
              leaderboard.map((player, index) => {
                const roundScore = selectedRound > 0 
                  ? player.roundScores.find(r => r.round === selectedRound)
                  : null;

                return (
                  <TableRow
                    key={player.id}
                    sx={{
                      backgroundColor: index === 0 ? 'rgba(255, 215, 0, 0.1)' : 'inherit',
                      '&:hover': { backgroundColor: 'action.hover' }
                    }}
                  >
                    <TableCell align="center">
                      <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                        {index === 0 && <EmojiEvents sx={{ color: 'gold', fontSize: 20 }} />}
                        <Typography variant="h6" fontWeight={index < 3 ? 'bold' : 'normal'}>
                          {player.position}
                        </Typography>
                        {getPositionChange(player.position, player.previousPosition)}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={index < 3 ? 'bold' : 'normal'}>
                        {player.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {player.completedRounds} of {numRounds} rounds
                      </Typography>
                    </TableCell>
                    <TableCell align="center">{player.handicap}</TableCell>
                    
                    {selectedRound === 0 ? (
                      <>
                        {Array.from({ length: numRounds }, (_, i) => (
                          <TableCell key={i + 1} align="center">
                            {getRoundScoreForDisplay(player, i + 1)}
                          </TableCell>
                        ))}
                        <TableCell align="center">
                          <Typography variant="h6" fontWeight={index < 3 ? 'bold' : 'normal'}>
                            {player.scoringScore}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={formatScoreToPar(player.scoringScore, coursePar * player.completedRounds)}
                            color={player.scoringScore < (coursePar * player.completedRounds) ? 'success' : 'error'}
                            size="small"
                          />
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell align="center">
                          {roundScore?.holes || 0}
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="h6">
                            {roundScore && roundScore.completed
                              ? (tournament.type === 'STROKEPLAY_SCRATCH' ? roundScore.gross : roundScore.net)
                              : '-'
                            }
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {roundScore && roundScore.completed && (
                            <Chip
                              label={formatScoreToPar(
                                tournament.type === 'STROKEPLAY_SCRATCH' ? roundScore.gross : roundScore.net,
                                coursePar
                              )}
                              color={
                                (tournament.type === 'STROKEPLAY_SCRATCH' ? roundScore.gross : roundScore.net) < coursePar
                                  ? 'success'
                                  : 'error'
                              }
                              size="small"
                            />
                          )}
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
}









